// ManageRoles.js
import React, { useState, useEffect } from 'react';

const emptyForm = { name: '', displayName: '', description: '', permissions: [] };

const ManageRoles = ({ backendUrl, resetKey }) => {
  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null); // role being edited
  const [isCreating, setIsCreating] = useState(false);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    fetchRoles();
    fetchCatalog();
    setSelectedRole(null);
    setIsCreating(false);
    setForm(emptyForm);
    // eslint-disable-next-line
  }, [resetKey]);

  const fetchRoles = async () => {
    const token = localStorage.getItem('auth-token');
    try {
      const response = await fetch(`${backendUrl}/roles`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', 'auth-token': token },
      });
      const result = await response.json();
      if (result.success) {
        setRoles(result.roles);
      } else {
        console.error('Error fetching roles:', result.message);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const fetchCatalog = async () => {
    const token = localStorage.getItem('auth-token');
    try {
      const response = await fetch(`${backendUrl}/roles/permissions-catalog`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', 'auth-token': token },
      });
      const result = await response.json();
      if (result.success) {
        setCatalog(result.catalog);
      }
    } catch (error) {
      console.error('Error fetching permission catalog:', error);
    }
  };

  const startCreate = () => {
    setForm(emptyForm);
    setIsCreating(true);
    setSelectedRole(null);
  };

  const startEdit = (role) => {
    setSelectedRole(role);
    setForm({
      name: role.name,
      displayName: role.displayName,
      description: role.description || '',
      permissions: role.permissions || [],
    });
    setIsCreating(false);
  };

  const backToList = () => {
    setSelectedRole(null);
    setIsCreating(false);
    setForm(emptyForm);
  };

  const togglePermission = (key) => {
    setForm((prev) => {
      const has = prev.permissions.includes(key);
      return {
        ...prev,
        permissions: has
          ? prev.permissions.filter((p) => p !== key)
          : [...prev.permissions, key],
      };
    });
  };

  const createRole = async () => {
    const token = localStorage.getItem('auth-token');
    if (!form.name || !form.displayName) {
      alert('Name and Display Name are required.');
      return;
    }
    try {
      const response = await fetch(`${backendUrl}/roles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'auth-token': token },
        body: JSON.stringify(form),
      });
      const result = await response.json();
      if (result.success) {
        alert('Role created successfully!');
        backToList();
        fetchRoles();
      } else {
        alert(result.message || 'Error creating role.');
      }
    } catch (error) {
      console.error('Error creating role:', error);
      alert('An error occurred while creating the role.');
    }
  };

  const updateRole = async () => {
    if (!selectedRole) return;
    const token = localStorage.getItem('auth-token');
    const body = {
      displayName: form.displayName,
      description: form.description,
    };
    // System roles: permissions are locked, don't send them
    if (!selectedRole.isSystem) {
      body.permissions = form.permissions;
    }
    try {
      const response = await fetch(`${backendUrl}/roles/${selectedRole._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'auth-token': token },
        body: JSON.stringify(body),
      });
      const result = await response.json();
      if (result.success) {
        alert('Role updated successfully!');
        backToList();
        fetchRoles();
      } else {
        alert(result.message || 'Error updating role.');
      }
    } catch (error) {
      console.error('Error updating role:', error);
      alert('An error occurred while updating the role.');
    }
  };

  const deleteRole = async (id) => {
    if (!window.confirm('Delete this role? It will be unassigned from all users.')) return;
    const token = localStorage.getItem('auth-token');
    try {
      const response = await fetch(`${backendUrl}/roles/${id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', 'auth-token': token },
      });
      const result = await response.json();
      if (result.success) {
        alert('Role deleted successfully!');
        backToList();
        fetchRoles();
      } else {
        alert(result.message || 'Error deleting role.');
      }
    } catch (error) {
      console.error('Error deleting role:', error);
      alert('An error occurred while deleting the role.');
    }
  };

  const isFullAccess = (role) => (role.permissions || []).includes('*');

  const renderForm = () => {
    const isSystem = selectedRole?.isSystem;
    const lockPermissions = isSystem; // system roles: permissions locked
    return (
      <div className="form-group">
        <h3>{isCreating ? 'Create New Role' : `Edit Role: ${selectedRole.displayName}`}</h3>

        {isCreating && (
          <>
            <label htmlFor="name">Name (internal id, lowercase, no spaces)</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. producer"
            />
          </>
        )}

        <label htmlFor="displayName">Display Name</label>
        <input
          type="text"
          value={form.displayName}
          onChange={(e) => setForm({ ...form, displayName: e.target.value })}
          placeholder="e.g. Producer"
        />

        <label htmlFor="description">Description</label>
        <input
          type="text"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          placeholder="What this role can do"
        />

        <label>Permissions (Dashboard sections)</label>
        {lockPermissions ? (
          <p style={{ color: '#a0aec0' }}>
            {selectedRole && isFullAccess(selectedRole)
              ? 'This system role has full access (*) and cannot be edited.'
              : 'This is a system role; its permissions cannot be edited.'}
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', margin: '8px 0' }}>
            {catalog.map((item) => (
              <label key={item.key} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={form.permissions.includes(item.key)}
                  onChange={() => togglePermission(item.key)}
                  style={{ width: 'auto', margin: 0 }}
                />
                {item.label} <span style={{ color: '#718096', fontSize: '12px' }}>({item.key})</span>
              </label>
            ))}
          </div>
        )}

        <div style={{ marginTop: '12px' }}>
          {isCreating ? (
            <button onClick={createRole}>Create Role</button>
          ) : (
            <button onClick={updateRole}>Save Changes</button>
          )}
          {!isCreating && selectedRole && !selectedRole.isSystem && (
            <button style={{ backgroundColor: 'red' }} onClick={() => deleteRole(selectedRole._id)}>
              Delete Role
            </button>
          )}
          <button onClick={backToList}>Back to List</button>
        </div>
      </div>
    );
  };

  return (
    <div className="form-group">
      <h2>Manage Roles</h2>

      {isCreating || selectedRole ? (
        renderForm()
      ) : (
        <>
          <table>
            <thead>
              <tr>
                <th>Display Name</th>
                <th>Name</th>
                <th style={{ textAlign: 'center' }}>Permissions</th>
                <th style={{ textAlign: 'center' }}>Type</th>
              </tr>
            </thead>
            <tbody>
              {roles.map((role) => (
                <tr key={role._id} onClick={() => startEdit(role)}>
                  <td>{role.displayName}</td>
                  <td>{role.name}</td>
                  <td style={{ textAlign: 'center' }}>
                    {isFullAccess(role) ? 'All (*)' : (role.permissions || []).length}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <span style={{
                      padding: '6px 16px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: '600',
                      background: role.isSystem ? '#667eea' : '#48bb78',
                      color: 'white',
                      display: 'inline-block',
                      minWidth: '70px',
                      textAlign: 'center',
                    }}>
                      {role.isSystem ? 'System' : 'Custom'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <button style={{ marginTop: '12px' }} onClick={startCreate}>Create New Role</button>
        </>
      )}
    </div>
  );
};

export default ManageRoles;
